"use client";

import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useSession } from "@/app/hooks/useSession";
import { AiCreditLimitBanner } from "@/components/shared/AiCreditLimitBanner";
import { trackPrepAheadGenerated } from "@/lib/analytics";
import {
  fadeSlideUpVariants,
  fadeVariants,
  timelineContentVariants,
  timelineDotVariants,
  timelineLineVariants,
} from "@/lib/animations";
import { addDays, localToday, toIsoDate } from "@/lib/calendar/week";
import type { DashboardEntry } from "@/lib/dashboard/server";
import type { PrepSuggestion } from "@/lib/llm/prep-ahead-prompt";
import { cn } from "@/lib/utils";

import { RecipeLinkRow } from "./RecipeLinkRow";

type PrepMode = "quick" | "best";

interface PrepAheadState {
  suggestions: PrepSuggestion[] | null;
  isStale: boolean;
  mode: PrepMode;
  usage: number;
  limit: number;
  loading: boolean;
  generating: boolean;
  error: string | null;
}

interface PrepAheadProps {
  recipeCount: number;
  entries: DashboardEntry[];
}

interface PlannedRecipe {
  id: string;
  title: string;
  image: string | null;
}

/** One recipe's prep steps within a day. `recipe` is null when it is no longer on this week's calendar. */
interface RecipeStepGroup {
  key: string;
  recipe: PlannedRecipe | null;
  title: string;
  steps: string[];
}

/**
 * A day's steps grouped by recipe, in order of first appearance. Steps with no recipe at all are plain text,
 * kept apart to show at the end of the day.
 */
function groupDayByRecipe(
  suggestions: PrepSuggestion[],
  recipeMap: Map<string, PlannedRecipe>
): { recipeGroups: RecipeStepGroup[]; loose: string[] } {
  const byKey = new Map<string, RecipeStepGroup>();
  const loose: string[] = [];
  for (const s of suggestions) {
    const recipe = s.recipeId ? (recipeMap.get(s.recipeId) ?? null) : null;
    const title = recipe?.title ?? s.recipeTitle ?? "";
    if (!title) {
      loose.push(s.text);
      continue;
    }
    const key = recipe?.id ?? (s.recipeId || title);
    const group = byKey.get(key) ?? { key, recipe, title, steps: [] };
    group.steps.push(s.text);
    byKey.set(key, group);
  }
  return { recipeGroups: [...byKey.values()], loose };
}

function computeDateLabel(
  date: string,
  todayIso: string,
  locale: string,
  t: (key: string, values?: Record<string, string>) => string
): string {
  const todayDate = new Date(todayIso + "T00:00:00Z");
  const tomorrowIso = toIsoDate(addDays(todayDate, 1));

  if (date === todayIso) return t("prepToday");
  if (date === tomorrowIso) return t("prepTomorrow");

  const dayName = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    timeZone: "UTC",
  }).format(new Date(date + "T00:00:00Z"));

  return dayName;
}

export function PrepAhead({ recipeCount, entries }: PrepAheadProps) {
  const t = useTranslations("dashboard");
  const tc = useTranslations("common");
  const locale = useLocale();
  const { isAuthenticated } = useSession();
  const [state, setState] = useState<PrepAheadState>({
    suggestions: null,
    isStale: false,
    mode: "best",
    usage: 0,
    limit: 5,
    loading: true,
    generating: false,
    error: null,
  });

  const fetchCached = useCallback(async () => {
    try {
      const res = await fetch(`/api/prep-ahead?locale=${locale}&today=${localToday()}`);
      if (!res.ok) return;
      const data = await res.json();
      setState((s) => ({
        ...s,
        suggestions: data.suggestions,
        isStale: data.isStale,
        mode: data.mode ?? "best",
        usage: data.usage,
        limit: data.limit,
        loading: false,
      }));
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [locale]);

  useEffect(() => {
    if (isAuthenticated && recipeCount >= 2) {
      setState((s) => (s.loading ? s : { ...s, loading: true }));
      fetchCached();
    } else if (isAuthenticated) {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [isAuthenticated, recipeCount, fetchCached]);

  const generate = useCallback(
    async (mode: PrepMode) => {
      setState((s) => ({ ...s, generating: true, error: null, mode }));
      try {
        const res = await fetch("/api/prep-ahead", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locale, mode, today: localToday() }),
        });
        const data = await res.json();
        if (!res.ok) {
          // Over the weekly limit: the limit banner shows in place of an error line.
          const limited = data.error === "aiUsageLimitReached";
          setState((s) => ({
            ...s,
            generating: false,
            usage: limited ? s.limit : s.usage,
            error: limited ? null : (data.error ?? "prepAheadFailed"),
          }));
          return;
        }
        trackPrepAheadGenerated();
        setState((s) => ({
          ...s,
          suggestions: data.suggestions,
          isStale: false,
          mode: data.mode ?? mode,
          usage: data.usage,
          limit: data.limit,
          generating: false,
        }));
      } catch {
        setState((s) => ({ ...s, generating: false, error: "prepAheadFailed" }));
      }
    },
    [locale]
  );

  const recipeMap = useMemo(() => {
    const map = new Map<string, PlannedRecipe>();
    for (const e of entries) {
      if (!map.has(e.recipe.id)) {
        map.set(e.recipe.id, { id: e.recipe.id, title: e.recipe.title, image: e.recipe.image });
      }
    }
    return map;
  }, [entries]);

  const localTodayIso = useMemo(() => localToday(), []);

  const activeSuggestions = useMemo(
    () => state.suggestions?.filter((s) => s.date && s.date >= localTodayIso) ?? [],
    [state.suggestions, localTodayIso]
  );

  const groupedByDate = useMemo(() => {
    if (activeSuggestions.length === 0) return [];
    const days: { date: string; label: string; suggestions: PrepSuggestion[] }[] = [];
    let currentDate = "";
    for (const s of activeSuggestions) {
      if (s.date !== currentDate) {
        currentDate = s.date;
        days.push({
          date: s.date,
          label: computeDateLabel(s.date, localTodayIso, locale, t as any),
          suggestions: [],
        });
      }
      days[days.length - 1].suggestions.push(s);
    }
    return days.map(({ date, label, suggestions }) => ({
      date,
      label,
      ...groupDayByRecipe(suggestions, recipeMap),
    }));
  }, [activeSuggestions, localTodayIso, locale, t, recipeMap]);

  if (!isAuthenticated || state.loading) return null;

  const remaining = state.limit - state.usage;
  const canGenerate = remaining > 0 && recipeCount >= 2;
  // A server error names a dashboard or a common message.
  const errorText = (error: string) => {
    const key = error as never;
    return t.has(key) ? t(key) : tc.has(key) ? tc(key) : t("prepAheadFailed");
  };
  const needsRecipes = recipeCount < 2;

  const hasSuggestions = activeSuggestions.length > 0;
  const hadSuggestions = state.suggestions && state.suggestions.length > 0;
  const allExpired = hadSuggestions && !hasSuggestions;

  return (
    <section aria-label={t("prepAhead")}>
      <div className="mb-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t("prepAhead")}
        </p>
        <h3 className="text-base font-semibold">{t("prepAheadSubtitle")}</h3>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <AnimatePresence mode="wait">
          {state.generating ? (
            <motion.div
              key="generating"
              variants={fadeSlideUpVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="flex items-center justify-center gap-2 p-4 py-8"
            >
              <Loader2 className="size-4 animate-spin text-primary" />
              <span className="text-muted-foreground">{t("generating")}</span>
            </motion.div>
          ) : hasSuggestions ? (
            <motion.div
              key="suggestions"
              variants={fadeVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="p-4"
            >
              {state.isStale && canGenerate && (
                <div className="mb-4 flex items-center gap-2">
                  <AlertCircle className="size-3.5 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">{t("prepStaleMessage")}</p>
                </div>
              )}
              <motion.ol
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="relative ms-1.5"
              >
                {groupedByDate.map((group, gi) => {
                  const isLastGroup = gi === groupedByDate.length - 1;
                  return (
                    <motion.li key={group.date} className="relative pb-6 ps-5 last:pb-0">
                      <motion.div
                        custom={gi}
                        variants={timelineDotVariants}
                        className="absolute -start-1.5 top-1 z-10 size-3 rounded-full border-2 border-primary bg-card"
                      />
                      {!isLastGroup && (
                        <motion.div
                          custom={gi}
                          variants={timelineLineVariants}
                          className="absolute start-0 top-2.5 -bottom-2.5 w-px origin-top bg-border"
                        />
                      )}
                      <motion.div custom={gi} variants={timelineContentVariants}>
                        <p className="font-semibold leading-tight text-primary">{group.label}</p>
                        <div
                          className={cn(
                            "flex flex-col gap-4",
                            group.recipeGroups.length > 0 ? "mt-2" : "mt-1"
                          )}
                        >
                          {group.recipeGroups.map((recipeGroup) => (
                            <div key={recipeGroup.key}>
                              <RecipeLinkRow
                                size="sm"
                                href={
                                  recipeGroup.recipe
                                    ? `/recipes/${recipeGroup.recipe.id}`
                                    : undefined
                                }
                                title={recipeGroup.title}
                                image={recipeGroup.recipe?.image ?? null}
                              />
                              <ul className="mt-1 space-y-2">
                                {recipeGroup.steps.map((text, si) => (
                                  <li key={si}>{text}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                          {group.loose.length > 0 && (
                            <ul
                              className={cn("space-y-2", group.recipeGroups.length > 0 && "pt-1")}
                            >
                              {group.loose.map((text, si) => (
                                <li key={si}>{text}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </motion.div>
                    </motion.li>
                  );
                })}
              </motion.ol>
              {remaining <= 0 && <AiCreditLimitBanner plain className="mt-2" />}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              variants={fadeSlideUpVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="flex flex-col items-center gap-3 p-4 py-8 text-center"
            >
              {needsRecipes ? (
                <p className="text-muted-foreground">{t("prepNeedRecipes")}</p>
              ) : allExpired && canGenerate ? (
                <>
                  <p className="text-muted-foreground">{t("prepExpired")}</p>
                  <ModeChips
                    activeMode={state.mode}
                    onSelect={(m) => generate(m)}
                    disabled={false}
                    t={t}
                  />
                </>
              ) : remaining <= 0 ? (
                <AiCreditLimitBanner plain className="py-0" />
              ) : (
                <ModeChips
                  activeMode={state.mode}
                  onSelect={(m) => generate(m)}
                  disabled={!canGenerate}
                  t={t}
                />
              )}

              {state.error && <p className="text-sm text-destructive">{errorText(state.error)}</p>}
            </motion.div>
          )}
        </AnimatePresence>

        {hasSuggestions && canGenerate && (
          <div className="flex justify-center border-t border-border px-4 py-3">
            <ModeChips
              activeMode={state.mode}
              onSelect={(m) => generate(m)}
              disabled={false}
              t={t}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function ModeChips({
  activeMode,
  onSelect,
  disabled,
  t,
}: {
  activeMode: PrepMode;
  onSelect: (mode: PrepMode) => void;
  disabled: boolean;
  t: (key: string) => string;
}) {
  const modes: PrepMode[] = ["quick", "best"];
  return (
    <div className="flex items-center justify-center gap-1.5">
      {modes.map((m) => (
        <button
          key={m}
          onClick={() => onSelect(m)}
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
            m === activeMode
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
            disabled && "pointer-events-none opacity-50"
          )}
        >
          <RefreshCw className="size-3" />
          {t(m === "quick" ? "prepModeQuick" : "prepModeBest")}
        </button>
      ))}
    </div>
  );
}
